/**
 * pdp-desktop-variants.js
 * Wires the custom desktop variant picker (.pdp-variant-btn) to:
 *  - the main product image (swaps via each button's own data-image-url)
 *  - the price block (custom_liquid "price_block", scoped by class only)
 *  - the buy-buttons form's hidden variant id input + submit button state
 *  - the URL (?variant=) for shareable/reloadable links
 *
 * Runs only on the desktop custom layout (#pdp-desktop-info). Everything is
 * looked up *inside* that container, so this never touches the mobile
 * .pdp-variant-wrap instance rendered separately under .pdp-mobile-only.
 */
(function () {
  'use strict';

  function init() {
    var infoWrap = document.getElementById('pdp-desktop-info');
    if (!infoWrap) {
      return;
    }

    var variantsScript = document.getElementById('pdp-desktop-variants-json');
    if (!variantsScript) {
      console.warn('[pdp-desktop-variants] No variants JSON script found.');
      return;
    }

    var variants;
    try {
      variants = JSON.parse(variantsScript.textContent);
    } catch (e) {
      console.error('[pdp-desktop-variants] Failed to parse variants JSON:', e);
      return;
    }

    if (!Array.isArray(variants) || variants.length === 0) {
      console.warn('[pdp-desktop-variants] Variants array is empty or invalid.');
      return;
    }

    // Scoped to the desktop panel only — never picks up the mobile instance.
    var variantWrap = infoWrap.querySelector('.pdp-variant-wrap');
    if (!variantWrap) {
      // Product has only one variant — nothing to wire up.
      return;
    }

    var priceWrap = infoWrap.querySelector('.pdp-price-wrap');

    // The buy_buttons block renders exactly one <form> inside this panel
    // (via Dawn's 'buy-buttons' snippet). Found by relative position, not
    // by a fixed id, so it survives changes to Dawn's internal markup.
    var atcForm = infoWrap.querySelector('form');
    var variantIdInput = atcForm ? atcForm.querySelector('input[name="id"]') : null;
    var atcBtn = atcForm ? atcForm.querySelector('button[type="submit"]') : null;
    var atcLabel = atcBtn ? atcBtn.querySelector('span') : null;

    // Track current selection per option position (1-indexed, matches Liquid's option.position)
    var selections = {};

    var activeButtons = variantWrap.querySelectorAll('.pdp-variant-btn.is-active');
    activeButtons.forEach(function (btn) {
      var pos = btn.getAttribute('data-option-position');
      selections[pos] = btn.getAttribute('data-value');
    });

    // Fallback: if nothing was marked is-active (e.g. markup changed), seed
    // selections from the first variant so matching still has a starting point.
    if (Object.keys(selections).length === 0 && variants[0] && variants[0].options) {
      variants[0].options.forEach(function (val, idx) {
        selections[idx + 1] = val;
      });
    }

    function findMatchingVariant() {
      var match = variants.find(function (v) {
        return Object.keys(selections).every(function (pos) {
          var idx = parseInt(pos, 10) - 1;
          return v.options[idx] === selections[pos];
        });
      });
      if (!match) {
        console.warn('[pdp-desktop-variants] No variant matched selections:', selections);
      }
      return match;
    }

    function formatMoney(cents) {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.theme && window.theme.moneyFormat) {
        return window.Shopify.formatMoney(cents, window.theme.moneyFormat);
      }
      var moneyFormat = window.theme && window.theme.moneyFormat;
      if (moneyFormat) {
        var amount = cents / 100;
        var noDecimals = Math.round(amount).toString();
        var withDecimals = amount.toFixed(2);
        return moneyFormat
          .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, '.'))
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withDecimals.replace('.', ','))
          .replace(/\{\{\s*amount\s*\}\}/g, withDecimals);
      }
      // Last resort fallback: strip unnecessary decimals for whole amounts.
      var existing = priceWrap ? priceWrap.textContent.match(/[^\d.,\s]+/) : null;
      var symbol = existing ? existing[0] : '';
      var a = cents / 100;
      return symbol + (a % 1 === 0 ? Math.round(a).toString() : a.toFixed(2));
    }

    function updatePrice(variant) {
      if (!priceWrap) return;
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        var saveAmount = variant.compare_at_price - variant.price;
        priceWrap.innerHTML =
          '<div class="pdp-price-row">' +
            '<span class="price-compare">' + formatMoney(variant.compare_at_price) + '</span>' +
            '<span class="price-sale">' + formatMoney(variant.price) + '</span>' +
          '</div>' +
          '<span class="price-save-text">Save ' + formatMoney(saveAmount) + '</span>';
      } else {
        priceWrap.innerHTML = '<span class="price-regular">' + formatMoney(variant.price) + '</span>';
      }
    }

    function updateAtc(variant) {
      if (variantIdInput) variantIdInput.value = variant.id;
      if (atcBtn) {
        atcBtn.disabled = !variant.available;
        // Best-effort text swap: only touches a <span> if the buy-buttons
        // markup happens to wrap its label in one. If it doesn't, the
        // disabled state above still correctly blocks add-to-cart.
        if (atcLabel) atcLabel.textContent = variant.available ? atcLabel.dataset.available || 'Add to cart' : 'Sold out';
      }
    }

    function updateMainImage(btn) {
      var imgUrl = btn.getAttribute('data-image-url');
      if (!imgUrl) return;
      var mainImg = document.getElementById('PdpMainImage');
      if (!mainImg) return;
      mainImg.src = imgUrl;
      var srcset = btn.getAttribute('data-image-srcset');
      if (srcset) mainImg.srcset = srcset;
    }

    function updateUrl(variant) {
      if (!window.history || !history.replaceState) return;
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url);
      } catch (e) {
        /* no-op */
      }
    }

    function selectVariant(variant) {
      if (!variant) return;
      updatePrice(variant);
      updateAtc(variant);
      updateUrl(variant);
    }

    variantWrap.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('.pdp-variant-btn') : null;
      if (!btn || !variantWrap.contains(btn)) return;

      var pos = btn.getAttribute('data-option-position');
      var value = btn.getAttribute('data-value');
      selections[pos] = value;

      var group = btn.closest('.pdp-variant-group');
      if (group) {
        var groupButtons = group.querySelectorAll('.pdp-variant-btn');
        groupButtons.forEach(function (b) {
          b.classList.remove('is-active');
        });
        btn.classList.add('is-active');

        var label = group.querySelector('.pdp-variant-selected-val');
        if (label) label.textContent = ': ' + value;
      }

      updateMainImage(btn);

      var matched = findMatchingVariant();
      selectVariant(matched);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();